import { ActionModal } from '@kcftech/design-system';
import {
    IProfileChild,
    IUserProfileUpdateRequest,
} from '@kcftech/prometheustypescriptapiclient';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { DateTimeOptionsPane } from 'components/user_menu/DateTimeOptionsPane';
import { OtherIndicatorOptionsPane } from 'components/user_menu/OtherIndicatorOptionsPane';
import { PersonaOptionsPane } from 'components/user_menu/PersonaOptionsPane';
import { VibrationOptionsPane } from 'components/user_menu/VibrationOptionsPane';
import { invalidateCurrentUser } from 'custom_hooks/data_hooks/user/useCurrentUserInfo';
import { useProfileSettingsOptions } from 'custom_hooks/data_hooks/user/useProfileSettingsOptions';
import { invalidateFrequencyDomainData } from 'pages/workbench/workbench_tabs/trend/frequency_domain/useFrequencyDomainData';
import { invalidateTimeDomainData } from 'pages/workbench/workbench_tabs/trend/time_domain/useTimeDomainData';
import { invalidateTrendTimeSeriesData } from 'pages/workbench/workbench_tabs/trend/timeseries/useTrendTimeSeriesData';
import { invalidateTrendIndicators } from 'pages/workbench/workbench_tabs/trend/useTrendIndicators';
import { invalidateWaterfallFrequencyData } from 'pages/workbench/workbench_tabs/trend/waterfall/useTrendWaterfallFrequencyData';
import { queryClient } from 'utils/query';
import { updateUserProfile } from 'utils/user';

export type UserProfileModalProps = Readonly<{
    userId: string;
    profile: IProfileChild;
    onClose: () => void;
}>;

const invalidateRelatedQueries = () => {
    invalidateCurrentUser();
    invalidateTrendIndicators();
    invalidateTrendTimeSeriesData();
    invalidateTimeDomainData();
    invalidateFrequencyDomainData();
    invalidateWaterfallFrequencyData();
    queryClient.invalidateQueries({
        queryKey: ['indicator-tab-tables'],
    });
};

export function UserProfileModal({ userId, profile, onClose }: UserProfileModalProps) {
    const { data: options, isLoading: optionsLoading } = useProfileSettingsOptions();

    const onSettingSave = async (updates: Partial<IUserProfileUpdateRequest>) => {
        try {
            await updateUserProfile(userId, updates);
            invalidateRelatedQueries();
        } catch {
            return 'Unable to save value.';
        }
        return undefined;
    };

    return (
        <ActionModal
            title="User Profile Settings"
            showActionButton={false}
            onClose={onClose}
        >
            <SpinnerArea isLoading={optionsLoading}>
                {options && (
                    <>
                        <DateTimeOptionsPane
                            options={options}
                            profile={profile}
                            onChange={onSettingSave}
                            className="mb-5"
                        />
                        <VibrationOptionsPane
                            options={options}
                            profile={profile}
                            onChange={onSettingSave}
                            className="mb-5"
                        />
                        <OtherIndicatorOptionsPane
                            options={options}
                            profile={profile}
                            onChange={onSettingSave}
                            className="mb-5"
                        />
                        <PersonaOptionsPane
                            options={options}
                            profile={profile}
                            onChange={onSettingSave}
                            className="mb-5"
                        />
                    </>
                )}
            </SpinnerArea>
        </ActionModal>
    );
}
